import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, FileText, ExternalLink, Eye, Plus, Pencil
} from 'lucide-react';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';
import GovernanceModal from '@/components/GovernanceModal';
import { toast } from 'sonner';

export default function ClientGovernance() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.getGovernance(token, clientId);
        setDocs(response.data);
      } catch (error) {
        console.error('Failed to fetch governance docs:', error);
      }
      setLoading(false);
    };
    fetchDocs();
  }, [token, clientId]);

  const handleAddDoc = () => {
    setEditingDoc(null);
    setShowDocModal(true);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setShowDocModal(true);
  };

  const handleSaveDoc = (docData) => {
    if (editingDoc) {
      // Edit existing document
      setDocs(prev => prev.map(d => 
        d.id === editingDoc.id 
          ? { ...d, ...docData }
          : d
      ));
      toast.success('Document updated successfully');
    } else {
      // Create new document
      const newDoc = {
        id: `gov-${clientId.split('-')[1]}-${Date.now()}`,
        clientId,
        ...docData
      };
      setDocs(prev => [...prev, newDoc]);
      toast.success('Document added successfully');
    }
    setShowDocModal(false);
    setEditingDoc(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="governance-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Governance</h1>
          <p className="text-[#5D524D]">Family office governance documents for {client?.name}</p>
        </div>
        <Button 
          className="caffeine-btn" 
          onClick={handleAddDoc}
          data-testid="add-governance-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Document
        </Button>
      </div>

      {/* Note */}
      <div className="bg-[#4A3B32]/5 border border-[#4A3B32]/20 rounded-lg p-4 mb-6">
        <p className="text-sm text-[#5D524D]">
          <strong>Note:</strong> Governance records stored securely; source documents are indexed.
        </p>
      </div>

      {/* Document Cards */}
      {docs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {docs.map(doc => (
            <div key={doc.id} className="doc-card" data-testid={`gov-doc-${doc.id}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#4A3B32]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#2C2420] mb-1">{doc.title}</h3>
                  <span className="text-xs px-2 py-0.5 bg-[#F0EBE6] text-[#5D524D] rounded">
                    {doc.category}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-[#8C8078] mb-4">
                <div className="flex justify-between">
                  <span>Source:</span>
                  <span className="source-chip">{doc.sourceSystem}</span>
                </div>
                <div className="flex justify-between">
                  <span>Owner:</span>
                  <span className="text-[#5D524D]">{doc.owner}</span>
                </div>
                <div className="flex justify-between">
                  <span>Last Updated:</span>
                  <span className="text-[#5D524D]">{formatDateAU(doc.lastUpdated)}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  className="flex-1 caffeine-btn-ghost justify-center"
                  onClick={() => setPreviewDoc(doc)}
                  data-testid={`preview-gov-doc-${doc.id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button 
                  variant="ghost" 
                  className="caffeine-btn-ghost justify-center"
                  onClick={() => handleEditDoc(doc)}
                  data-testid={`edit-gov-doc-${doc.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  className="caffeine-btn-ghost justify-center"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FileText className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No governance documents</p>
          <p className="text-sm">No governance documents indexed for this client</p>
          <Button 
            className="caffeine-btn mt-4" 
            onClick={handleAddDoc}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Document
          </Button>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          type="governance"
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Add/Edit Document Modal */}
      {showDocModal && (
        <GovernanceModal
          document={editingDoc}
          onClose={() => {
            setShowDocModal(false);
            setEditingDoc(null);
          }}
          onSave={handleSaveDoc}
        />
      )}
    </div>
  );
}
